import React from 'react';
import { Link } from 'react-router-dom';
import { QrCodeIcon, FileText, AlertTriangle, CheckCircle, XCircle, Scale } from 'lucide-react';

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <QrCodeIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">iComment.app</span>
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="text-gray-600 hover:text-gray-900">Home</Link>
              <Link to="/auth" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Sign In</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <FileText className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Terms of Service
            <span className="block text-yellow-300 text-3xl md:text-4xl mt-4">Legal Agreement</span>
          </h1>
          <p className="text-xl mb-4">Last updated: January 1, 2025</p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
          
          {/* Agreement to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Scale className="h-6 w-6 mr-2 text-blue-600" />
              Agreement to Terms
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              By accessing and using iComment.app ("Service"), you accept and agree to be bound by the terms 
              and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
            </p>
            <p className="text-gray-600 leading-relaxed">
              These Terms of Service ("Terms") govern your use of our website located at icomment.app operated by 
              iComment.app Inc. ("us", "we", or "our").
            </p>
          </section>

          {/* Service Description */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Description</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              iComment.app provides anonymous two-way messaging services through QR codes. Users can generate QR codes 
              that allow others to send anonymous messages while maintaining privacy for both parties.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">Key Features:</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>QR code generation for anonymous messaging</li>
                <li>Email and SMS notifications</li>
                <li>Two-way communication while maintaining anonymity</li>
                <li>Multiple subscription tiers with different usage limits</li>
              </ul>
            </div>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">User Accounts</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                  Account Requirements
                </h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>You must provide accurate and complete information</li>
                  <li>You must be at least 13 years old to use the service</li>
                  <li>You are responsible for maintaining account security</li>
                  <li>One account per person or organization</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <XCircle className="h-5 w-5 mr-2 text-red-600" />
                  Account Restrictions
                </h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>No sharing of account credentials</li>
                  <li>No creating multiple accounts to circumvent limits</li>
                  <li>No using automated systems to create accounts</li>
                  <li>No impersonating others or providing false information</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="h-6 w-6 mr-2 text-yellow-600" />
              Acceptable Use Policy
            </h2>
            
            <div className="bg-green-50 p-4 rounded-lg mb-4">
              <h3 className="font-semibold text-gray-900 mb-2 text-green-800">✅ Permitted Uses:</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Lost and found item communication</li>
                <li>Anonymous feedback collection</li>
                <li>Property and vehicle contact information</li>
                <li>Business customer communication</li>
                <li>Educational and event communications</li>
                <li>Any lawful communication purpose</li>
              </ul>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2 text-red-800">❌ Prohibited Uses:</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Harassment, threats, or abusive behavior</li>
                <li>Spam or unsolicited commercial messages</li>
                <li>Illegal activities or content</li>
                <li>Copyright or trademark infringement</li>
                <li>Hate speech or discriminatory content</li>
                <li>Malware, viruses, or harmful code distribution</li>
                <li>Attempting to access other users' accounts</li>
              </ul>
            </div>
          </section>

          {/* Subscription and Billing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Subscription and Billing</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Subscription Plans</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li><strong>Free:</strong> Limited QR codes and messages</li>
                  <li><strong>Basic ($10/year):</strong> Increased limits and additional features</li>
                  <li><strong>Premium ($49/year):</strong> Unlimited usage and premium features</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Billing Terms</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Subscriptions are billed annually in advance</li>
                  <li>No refunds for partial months or unused features</li>
                  <li>30-day money-back guarantee for new subscriptions</li>
                  <li>Automatic renewal unless cancelled</li>
                  <li>Price changes will be communicated 30 days in advance</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Content and Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Content and Privacy</h2>
            <div className="space-y-4">
              <p className="text-gray-600 leading-relaxed">
                <strong>Your Content:</strong> You retain ownership of all content you create or share through 
                the service. By using our service, you grant us a limited license to process and deliver your content.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>Privacy Protection:</strong> We are committed to protecting your privacy. We will never share 
                your personal contact information with message senders. See our Privacy Policy for full details.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>Content Moderation:</strong> We reserve the right to remove content that violates these terms 
                or applicable law. We may suspend or terminate accounts that repeatedly violate our policies.
              </p>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-gray-600 leading-relaxed mb-4">
                The service is provided "as is" without warranties of any kind. We do not guarantee that the service 
                will be uninterrupted, secure, or error-free.
              </p>
              <p className="text-gray-600 leading-relaxed">
                To the fullest extent permitted by law, iComment.app shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages, or any loss of profits or revenues.
              </p>
            </div>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Termination</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">By You</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                  <li>Cancel subscription anytime</li>
                  <li>Delete account and data</li>
                  <li>Download your data before deletion</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">By Us</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                  <li>Violation of terms</li>
                  <li>Illegal or harmful activity</li>
                  <li>Non-payment of fees</li>
                  <li>At our discretion with notice</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              We reserve the right to modify these terms at any time. We will notify users of material changes 
              via email or through the service. Continued use of the service after changes constitutes acceptance 
              of the new terms.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-600 mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="space-y-2 text-gray-600">
              <p><strong>Email:</strong> legal@icomment.app</p>
              <p><strong>Address:</strong> 123 Tech Street, San Francisco, CA 94105</p>
              <p><strong>Support:</strong> <Link to="/contact" className="text-blue-600 hover:underline">Contact Form</Link></p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}